import styled from "@emotion/styled";
import memoizeOne from "memoize-one";
import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";

import { SpacingToken } from "@livechat/design-system-react-components";

import { IAvailableFilter, withFiltersData } from "components/filters";
import { Filters } from "components/filters/Filters";
import { SaveCustomView } from "components/filters/SaveCustomView";
import { IFilterUpdate } from "components/filters/interfaces";
import Block from "components/layout/Block";
import { Filter, IFilters, notTaggedFilter } from "constants/filters";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { availableFiltersRouteMap } from "constants/tickets/filters";
import { useToggle } from "hooks/use-toggle";
import AddCustomViewModal from "routes/tickets/AddCustomViewModal/AddCustomViewModal";
import { IAgent } from "store/entities/agents";
import { selectLanguageLabels } from "store/entities/application-settings/selectors";
import { selectAvailableSources } from "store/entities/license";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { TicketsViewActions } from "store/views/tickets/actions";
import { selectQualifiesForSavingAsCustomView } from "store/views/tickets/selectors";
import { withPx, withVar } from "themes/helpers";

const FiltersWithData = withFiltersData(Filters);

function calculateAvailableItems(route: TicketsRoutes, tags: ITag[]): IAvailableFilter[] {
  let availableFilters = availableFiltersRouteMap[route];
  if (tags.length === 0) {
    availableFilters = availableFilters.filter((f: IAvailableFilter) => f.filter !== Filter.Tag);
  }
  return availableFilters;
}

const getAvailableFilters = memoizeOne(calculateAvailableItems);

const calculateTags = (tags: ITag[]): ITag[] => [notTaggedFilter].concat(tags);

const getTags = memoizeOne(calculateTags);

interface IProps {
  route: TicketsRoutes;
  filters: IFilters;
  filtersOrder: Filter[];
  agents: IAgent[];
  currentAgent: IAgent;
  teams: ITeam[];
  allTeams: ITeam[];
  teamsUsageAllowed: boolean;
  tags: ITag[];
  allTags: ITag[];
  onFilterUpdate(filterUpdate: IFilterUpdate): void;
}

export const TicketsFilters: FC<IProps> = ({
  route,
  filtersOrder,
  agents,
  currentAgent,
  teams,
  allTeams,
  teamsUsageAllowed,
  tags,
  allTags,
  filters,
  onFilterUpdate,
}) => {
  const dispatch = useDispatch();
  const qualifiesForSavingAsCustomView = useSelector(selectQualifiesForSavingAsCustomView);
  const languageLabels = useSelector(selectLanguageLabels);
  const availableSources = useSelector(selectAvailableSources);

  const onCreateCustomView = (name: string, shared?: boolean): void => {
    dispatch(TicketsViewActions.createCustomView(name, shared ?? false));
    toggleSaveCustomFilterModalOpen();
  };
  const [saveCustomFilterModalOpen, toggleSaveCustomFilterModalOpen] = useToggle(false);

  return (
    <>
      <FilterWrapper>
        <FiltersWithData
          availableFilters={getAvailableFilters(route, tags)}
          disabledFilters={teamsUsageAllowed ? [] : [Filter.Group]}
          filters={filters}
          filtersOrder={filtersOrder}
          agents={agents}
          groups={teams}
          allGroups={allTeams}
          tags={getTags(tags)}
          allTags={allTags}
          currentAgent={currentAgent}
          onFilterUpdate={onFilterUpdate}
          availableSources={availableSources}
          languageLabels={languageLabels}
        />
        {qualifiesForSavingAsCustomView && (
          <Block marginLeft={withVar(SpacingToken.Spacing2)}>
            <SaveCustomView onCreateCustomView={toggleSaveCustomFilterModalOpen} />
          </Block>
        )}
      </FilterWrapper>
      {saveCustomFilterModalOpen && (
        <AddCustomViewModal
          onCreate={onCreateCustomView}
          onClose={toggleSaveCustomFilterModalOpen}
          filters={filters}
          languageLabels={languageLabels}
          filtersOrder={filtersOrder}
          agents={agents}
          teams={teams}
          tags={getTags(tags)}
          route={route}
        />
      )}
    </>
  );
};

const FilterWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  @media (min-width: ${withPx(ResponsiveBreakpoint.Mobile)}) {
    overflow-x: initial;
  }
`;
